// @ts-ignore
import * as L from 'leaflet/dist/leaflet-src.esm.js';
// Based on plugin in https://www.npmjs.com/package/leaflet-switch-scale-control
// which is based on https://github.com/Flexberry/leaflet-switch-scale-control
// Other option for calculating scales could be to use the way described in this link:
// https://gis.stackexchange.com/questions/423633/calculating-leaflet-scale-ratio-for-the-particular-zoom-level
// This scale is an approximation of the real value and only works for
// projection EPSG:3857
export const addSwitchScale = () => {
    L.Control.SwitchScaleControl = L.Control.extend({
        // Last calculated scale
        lastValue: null,
        options: {
            position: 'bottomleft',
            dropdownDirection: 'upward',
            className: 'map-control-scalebar',
            updateWhenIdle: false,
            ratio: true,
            ratioPrefix: '1:',
            ratioCustomItemText: '1:',
            customScaleTitle: '',
            ratioMenu: true,
            // If recalcOnZoomChange is false, then recalcOnPositionChange is always false.
            recalcOnPositionChange: false,
            recalcOnZoomChange: false,
            roundScales: null,
            adjustScales: false,
            splitScale: false,
            // Returns pixels per meter; needed if ratio: true.
            pixelsInMeterWidth() {
                const div = document.createElement('div');
                div.style.cssText = 'position: absolute;  left: -100%;  top: -100%;  width: 100cm;';
                document.body.appendChild(div);
                const px = div.offsetWidth;
                document.body.removeChild(div);
                return px;
            },
            // Returns width of map in meters on specified latitude.
            getMapWidthForLanInMeters(currentLan) {
                return 6378137 * 2 * Math.PI * Math.cos((currentLan * Math.PI) / 180);
            },
        },
        /**
         * Add control to map
         * @param map Leaflet map
         * @returns The div container of scale
         */
        onAdd(map) {
            this.map = map;
            this.pixelsInMeterWidth = this.options.pixelsInMeterWidth();
            const { className } = this.options;
            const container = L.DomUtil.create('div', `leaflet-control-scale ${className}`);
            const { options } = this;
            this.addScales(options, className, container);
            if (options.recalcOnZoomChange) {
                if (options.recalcOnPositionChange) {
                    map.on(options.updateWhenIdle ? 'moveend' : 'move', this.update, this);
                }
                else {
                    map.on(options.updateWhenIdle ? 'zoomend' : 'zoom', this.update, this);
                }
            }
            else {
                map.on(options.updateWhenIdle ? 'zoomend' : 'zoom', this.updateRound, this);
            }
            map.whenReady(options.recalcOnZoomChange ? this.update : this.updateRound, this);
            L.DomEvent.disableClickPropagation(container);
            return container;
        },
        /**
         * On remove, delete events
         * @param map The leaflet map
         */
        onRemove(map) {
            if (this.options.recalcOnZoomChange) {
                if (this.options.recalcOnPositionChange) {
                    map.off(this.options.updateWhenIdle ? 'moveend' : 'move', this.update, this);
                }
                else {
                    map.off(this.options.updateWhenIdle ? 'zoomend' : 'zoom', this.update, this);
                }
            }
            else {
                map.off(this.options.updateWhenIdle ? 'zoomend' : 'zoom', this.updateRound, this);
            }
        },
        /**
         * Sets the zoom for the desired scale
         * @param ratio Ratio.
         */
        setScale(ratio) {
            this.fixedScale = ratio;
            const bounds = this.map.getBounds();
            const centerLat = bounds.getCenter().lat;
            const crsScale = (this.pixelsInMeterWidth * this.options.getMapWidthForLanInMeters(centerLat)) / ratio;
            this.map.setZoom(this.map.options.crs.zoom(crsScale));
            this.toggleDropdown();
        },
        /**
         * Show or hide input
         */
        toggleDropdown() {
            const height = this.dropdown.style['max-height'] === '0px' ? '50' : 0;
            this.dropdown.style['max-height'] = `${height}px`;
            this.dropdown.style.border = height ? null : '0';
            if (this.customScaleInput) {
                this.customScaleInput.value = this.options.ratioPrefix + this.lastValue;
            }
        },
        /**
         * Add scale and input
         * @param options Options of switch scale
         * @param className Class name of swithc
         * @param container Container
         * @returns Nothing
         */
        addScales(options, className, container) {
            if (!options.ratio)
                return;
            if (options.ratioMenu) {
                this.dropdown = L.DomUtil.create('div', `${className}-dropdown`, container);
                this.toggleDropdown();
            }
            // Add scale text
            this.text = L.DomUtil.create('div', `${className}-text`, container);
            if (!options.ratioMenu)
                return;
            const self1 = this;
            this.text.addEventListener('click', this.toggleDropdown.bind(self1));
            // Add input
            this.customScaleInput = L.DomUtil.create('input', `${className}-custom-scale`, this.dropdown);
            this.customScaleInput.type = 'text';
            this.customScaleInput.setAttribute('value', options.ratioCustomItemText);
            this.customScaleInput.addEventListener('focus', (e) => {
                const self = this.customScaleInput;
                if (self.value === options.ratioCustomItemText) {
                    self.value = options.ratioPrefix;
                    // IE fix.
                    if (self.createTextRange) {
                        const r = self.createTextRange();
                        r.moveStart('character', self.value.length);
                        r.select();
                    }
                }
                e.stopPropagation();
            });
            // Add input close button
            const closeButton = L.DomUtil.create('span', `${className}-custom-scale-close-button material-icons-round dinapsis-map-icon close`, this.dropdown);
            closeButton.innerHTML = 'X';
            closeButton.addEventListener('click', this.toggleDropdown.bind(self1));
            this.customScaleInput.addEventListener('keydown', (e) => {
                if (e.which !== 13)
                    return;
                const scaleRatioFound = this.customScaleInput.value.replace(' ', '').replace('\'', '').match(/^(1:){0,1}([0-9]*)$/);
                if (scaleRatioFound && scaleRatioFound[2]) {
                    self1.setScale.call(self1, scaleRatioFound[2]);
                }
                e.preventDefault();
            });
            this.customScaleInput.addEventListener('keypress', (e) => {
                if (e.charCode && (e.charCode < 48 || e.charCode > 57))
                    e.preventDefault();
            });
        },
        /**
         * Update scale rounding value
         */
        updateRound() {
            this.updateFunction(true);
        },
        /**
         * Update scale not rounding value
         */
        update() {
            this.updateFunction(false);
        },
        /**
         * Update the scales
         * @param isRound If we want to round scale
         */
        updateFunction(isRound) {
            const bounds = this.map.getBounds();
            const { options } = this;
            const centerLat = bounds.getCenter().lat;
            const size = this.map.getSize();
            let physicalScaleRatio = 0;
            if (size.x > 0) {
                if (options.ratio) {
                    physicalScaleRatio = (this.pixelsInMeterWidth * options.getMapWidthForLanInMeters(centerLat)) / this.map.options.crs.scale(this.map.getZoom());
                }
            }
            this.updateScales(options, physicalScaleRatio, isRound);
        },
        /**
         * Update the scale
         * @param options Control options
         * @param physicalScaleRatio The scale value
         * @param isRound If we want to round value
         */
        updateScales(options, physicalScaleRatio, isRound) {
            if (options.ratio && physicalScaleRatio) {
                this.updateRatio(physicalScaleRatio, isRound);
            }
        },
        /**
         * The ratio to update
         * @param physicalScaleRatio The scale value
         * @param isRound If we want to round value
         */
        updateRatio(physicalScaleRatio, isRound) {
            if (this.fixedScale) {
                this.text.innerHTML = this.options.ratioPrefix + this.fixedScale;
                this.lastValue = this.fixedScale;
                this.fixedScale = null;
            }
            else {
                const scaleText = isRound
                    ? this.roundScale(physicalScaleRatio)
                    : Math.round(physicalScaleRatio);
                this.text.innerHTML = this.options.ratioPrefix + scaleText;
                this.lastValue = scaleText;
            }
        },
        /**
         * Round the scale value to integer
         * @param physicalScaleRatio The scale value
         * @returns Value rounded
         */
        roundScale(physicalScaleRatio) {
            return Math.round(physicalScaleRatio);
        },
    });
};
//# sourceMappingURL=switchScaleControl.js.map